import { useMutation, useQuery } from '@tanstack/react-query';
import { wahaClient } from '@/services/waha-client';
import {
  WAMediaDownloadRequest,
  WAMediaDownloadResponse,
  WAMediaMessage,
} from '@/services/interface';

/**
 * Hook for WhatsApp media operations (download, manage media files)
 */
export function useWhatsappMedia(sessionName: string = 'default') {
  // Download media from a message
  const downloadMediaMutation = useMutation<
    WAMediaDownloadResponse,
    Error,
    Omit<WAMediaDownloadRequest, 'session'>
  >({
    mutationFn: async ({ messageId }) => {
      return await wahaClient.getSessionResource<WAMediaDownloadResponse>(
        sessionName,
        `/chats/message-media?messageId=${messageId}`,
      );
    },
  });

  // Get media as a direct URL for a specific message
  const useMediaUrl = (messageId: string | undefined) => {
    return useQuery({
      queryKey: ['mediaUrl', sessionName, messageId],
      queryFn: async () => {
        const response = await wahaClient.getSessionResource<Blob>(
          sessionName,
          `/chats/message-media?messageId=${messageId}`,
          { responseType: 'blob' },
        );
        return URL.createObjectURL(response);
      },
      enabled: !!messageId,
      staleTime: 24 * 60 * 60 * 1000, // 24 hours - media usually doesn't change
      gcTime: 30 * 60 * 1000, // 30 minutes
    });
  };

  // Inside the useWhatsappMedia hook, add this mutation
  const sendMediaMutation = useMutation<
    any,
    Error,
    Omit<WAMediaMessage, 'session'>
  >({
    mutationFn: async (mediaMessage) => {
      // Use the correct endpoint
      return await wahaClient.post('/api/sendImage', {
        ...mediaMessage,
        session: sessionName,
      });
    },
  });

  // New dedicated mutation for sending files (PDF, DOCX, etc.)
  const sendFileMutation = useMutation<
    any,
    Error,
    Omit<WAMediaMessage, 'session'>
  >({
    mutationFn: async (fileMessage) => {
      // Use the sendFile endpoint for documents
      return await wahaClient.post('/api/sendFile', {
        ...fileMessage,
        session: sessionName,
      });
    },
  });

  // New mutation for sending voice messages
  const sendVoiceMutation = useMutation<
    any,
    Error,
    Omit<WAMediaMessage, 'session'>
  >({
    mutationFn: async (voiceMessage) => {
      // Use the sendVoice endpoint for audio messages
      return await wahaClient.post('/api/sendVoice', {
        ...voiceMessage,
        session: sessionName,
      });
    },
  });

  // New mutation for sending video messages
  const sendVideoMutation = useMutation<
    any,
    Error,
    Omit<WAMediaMessage, 'session'>
  >({
    mutationFn: async (videoMessage) => {
      // Use the sendVideo endpoint for video messages
      return await wahaClient.post('/api/sendVideo', {
        ...videoMessage,
        session: sessionName,
      });
    },
  });

  return {
    downloadMedia: downloadMediaMutation.mutateAsync,
    isDownloading: downloadMediaMutation.isPending,
    useMediaUrl,
    sendMedia: sendMediaMutation.mutateAsync,
    isSendingMedia: sendMediaMutation.isPending,
    // Add these new exports for file handling
    sendFile: sendFileMutation.mutateAsync,
    isSendingFile: sendFileMutation.isPending,
    // Add these new exports for voice handling
    sendVoice: sendVoiceMutation.mutateAsync,
    isSendingVoice: sendVoiceMutation.isPending,
    // Video handling
    sendVideo: sendVideoMutation.mutateAsync,
    isSendingVideo: sendVideoMutation.isPending,
  };
}
